/**
 * ActionApprovePage - Handle structural action approve email links
 * 
 * Flow:
 * 1. Extract token from URL
 * 2. Validate and show action details
 * 3. Process approval
 * 4. Show result and remove token from URL
 */
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { EmailActionResultPage } from "./EmailActionResultPage";
import api from "../lib/api";

export default function ActionApprovePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading");
  const [title, setTitle] = useState(null);
  const [message, setMessage] = useState(null);
  const [contextInfo, setContextInfo] = useState(null);
  const [processed, setProcessed] = useState(false);

  // Remove token from URL after processing
  const removeTokenFromUrl = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Process token
  useEffect(() => {
    if (!token) {
      setStatus("error");
      setTitle("Invalid link");
      setMessage("This link is missing required information.");
      return;
    }

    if (processed) return;

    const processToken = async () => {
      try {
        // First validate the token
        const validateRes = await api.get(`/api/household/token/validate?token=${encodeURIComponent(token)}`);
        
        if (validateRes.ok) {
          const validateData = await validateRes.json();
          
          if (validateData.valid && validateData.context) {
            setContextInfo(validateData.context);
            
            // Check if already handled
            if (validateData.context.status !== "pending") {
              setStatus("already_handled");
              setTitle("Already processed");
              setMessage(`This change request has been ${validateData.context.status}.`);
              removeTokenFromUrl();
              setProcessed(true);
              return;
            }
          }
        }

        // Process the approve action
        const res = await api.post(`/api/household/token/action/approve?token=${encodeURIComponent(token)}`);
        const data = await res.json();

        if (res.ok) {
          if (data.status === "already_handled") {
            setStatus("already_handled");
            setTitle("Already processed");
            setMessage(data.message);
          } else {
            setStatus("success");
            setTitle("Change approved");
            setMessage("The requested change has been approved and applied to your household.");
          }
        } else if (res.status === 410) {
          setStatus("expired");
          setTitle("Link expired");
          setMessage(data.detail || "This approval link is no longer valid. You can approve changes from the app.");
        } else {
          setStatus("error");
          setMessage(data.detail || "Could not approve this change.");
        }

        removeTokenFromUrl();
        setProcessed(true);

      } catch (error) {
        console.error("Action approve error:", error);
        setStatus("error");
        setMessage("Something went wrong. Please try again from the app.");
        removeTokenFromUrl();
      }
    };

    processToken();
  }, [token, processed, removeTokenFromUrl]);

  return (
    <EmailActionResultPage
      status={status}
      title={title}
      message={message}
      primaryButtonLabel="Go to Dashboard"
      primaryButtonAction="/"
      contextInfo={contextInfo}
      showHomeButton={false}
    />
  );
}
